import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Card,
  Input,
  Tooltip,
  Grid,
  List,
  Select,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  CopyOutlined,
  SearchOutlined,
  CalendarOutlined,
  UserOutlined,
  ShoppingOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { fetchOrders } from "../api/ordersApi";
import {
  getStatusInJapanese,
  getStatusBadgeColor,
  getOriginNames,
} from "../utils/orderDisplay";
import "../styles/OrderList.css";
import type { OrderResponse as Order } from "../model/types";
import type { ShodanOrderResponse as ShodanOrder } from "../../../entities/shodan-order/model/types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PATHS } from "../../../app/router/paths";
import { MobilePagination } from "../../../shared/components/MobilePagination";
import { fetchShodanOrders as fetchShoDanOrder } from "../../../entities/shodan-order/api/shodanOrdersApi";
import { getProductNames } from "../utils/orderDisplay";
import { formatApiTimestamp } from "../../../shared/utils/apiTimestamp";
import { normalizeApiError } from "../../../shared/errors/normalizeApiError";
import { appNotification } from "../../../shared/notifications/appNotification";
import { usePageRefresh } from "../../../shared/pull-refresh/PullRefreshContext";
import { formatYen } from "../../../shared/utils/price";

const { Text } = Typography;
const { useBreakpoint } = Grid;

function generateShodanOrderCode(
  deliveryDate: string,
  orderNumber: string,
): string {
  const date = dayjs(deliveryDate).format("YYMMDD");
  return `SDO-${date}-${orderNumber}`;
}

const sanitizeOrderData = (ordersData: any[]): Order[] =>
  ordersData.map((order) => ({
    ...order,
    total_amount: Number(order.total_amount || 0),
    order_items: (order.order_items || []).map((line: any) => ({
      ...line,
      order_quantity: Number(line.order_quantity || 0),
      proposed_price: Number(line.proposed_price || 0),
      line_total: Number(line.line_total || 0),
    })),
  }));

const TruncatedCell = ({ children }: { children: React.ReactNode }) => (
  <Tooltip title={children}>
    <div
      style={{
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
      }}
    >
      {children}
    </div>
  </Tooltip>
);

const OrderList = () => {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const registerPageRefresh = usePageRefresh();
  const [searchParams, setSearchParams] = useSearchParams();

  const [orders, setOrders] = useState<(Order | ShodanOrder)[]>([]);

  // Read initial states from URL
  const [sourceOrder, setSourceOrder] = useState<string>(
    searchParams.get("source") || "order",
  );
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(
    searchParams.get("search") || "",
  );
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1,
  );
  const [pageSize, setPageSize] = useState(
    Number(searchParams.get("size")) || 10,
  );
  const [status, setStatus] = useState<string | undefined>(
    searchParams.get("status") || undefined,
  );

  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);
  const activeSourceOrderRef = useRef(sourceOrder);

  // Sync state changes to URL
  const updateSearchParams = useCallback(
    (updates: Record<string, string | number | undefined>) => {
      const newParams = new URLSearchParams();
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
          newParams.set(key, String(value));
        }
      });
      setSearchParams(newParams, { replace: true });
    },
    [setSearchParams],
  );

  const getRowKey = useCallback(
    (record: Order | ShodanOrder) => {
      if (sourceOrder === "shodan") {
        const shodan = record as ShodanOrder;
        return [
          shodan.customer_code,
          shodan.branch_code,
          shodan.delivery_date,
          shodan.order_number,
        ].join("|");
      }
      const order = record as Order;
      return String(order.id ?? order.order_number);
    },
    [sourceOrder],
  );

  const loadOrders = useCallback(
    async (
      requestedSource: string,
      page: number,
      size: number,
      search?: string,
      requestedStatus?: string,
    ) => {
      activeSourceOrderRef.current = requestedSource;

      try {
        setLoading(true);
        setOrders([]);

        const data =
          requestedSource === "order"
            ? await fetchOrders(page, size, search, requestedStatus)
            : await fetchShoDanOrder(page, size, search, requestedStatus);

        if (activeSourceOrderRef.current !== requestedSource) return;

        const resultsWithStatus = (data.items || []).map((result: any) => ({
          ...result,
          status: result.status ?? "confirmed",
        }));

        setOrders(sanitizeOrderData(resultsWithStatus));
        setTotal(data.pagination?.total || 0);
      } catch (err) {
        const apiError = normalizeApiError(err);
        appNotification.error({
          message: "注文一覧を取得できませんでした",
          description: apiError.message,
        });
      } finally {
        if (activeSourceOrderRef.current === requestedSource) {
          setLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    activeSourceOrderRef.current = sourceOrder;
  }, [sourceOrder]);

  useEffect(() => {
    updateSearchParams({
      source: sourceOrder,
      page: currentPage,
      size: pageSize,
      search: debouncedSearchText,
      status,
    });
  }, [
    sourceOrder,
    currentPage,
    pageSize,
    debouncedSearchText,
    status,
    updateSearchParams,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1); // Reset page on text type
    }, 500);
    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    loadOrders(sourceOrder, currentPage, pageSize, debouncedSearchText, status);
  }, [
    currentPage,
    pageSize,
    debouncedSearchText,
    status,
    sourceOrder,
    loadOrders,
  ]);

  useEffect(() => {
    registerPageRefresh({ enabled: true, refresh: () => loadOrders(sourceOrder, currentPage, pageSize, debouncedSearchText, status) });
    return () => registerPageRefresh(null);
  }, [registerPageRefresh, loadOrders, sourceOrder, currentPage, pageSize, debouncedSearchText, status]);

  const handleViewOrder = (record: any) => {
    if (sourceOrder === "order") {
      navigate(
        `${PATHS.ORDERS.VIEW}?id=${record.id}&type=${record.type || ""}&source=order`,
      );
    } else {
      const qs = new URLSearchParams({
        source: "shodan",
        customer_code: record.customer_code || "",
        branch_code: record.branch_code || "",
        order_number: record.order_number || "",
        delivery_date: record.delivery_date || "",
      }).toString();
      navigate(`${PATHS.ORDERS.VIEW}?${qs}`);
    }
  };

  const handleEditOrder = (record: any) => {
    if (sourceOrder === "order") {
      navigate(
        `${PATHS.ORDERS.EDIT}?id=${record.id}&type=${record.type || ""}`,
      );
    }
  };

  const handleCopyOrder = (record: any) => {
    if (sourceOrder === "order") {
      navigate(
        `${PATHS.ORDERS.EDIT}?copy=true&id=${record.id}&type=${record.type || ""}`,
      );
    } else {
      const qs = new URLSearchParams({
        copy: "true",
        source: "shodan",
        customer_code: record.customer_code || "",
        branch_code: record.branch_code || "",
        order_number: record.order_number || "",
        delivery_date: record.delivery_date || "",
      }).toString();
      navigate(`${PATHS.ORDERS.EDIT}?${qs}`);
    }
  };

  const handleNewSingleOrder = () => {
    navigate(PATHS.ORDERS.CREATE_SINGLE);
  };

  const handleNewMultiOrder = () => {
    navigate(PATHS.ORDERS.CREATE_MULTI);
  };

  const columns = [
    {
      title: "注文番号",
      dataIndex: "order_number",
      key: "order_number",
      minWidth: 100,
      width: 220,
      fixed: screens.lg ? ("left" as const) : undefined,
      render: (_: any, record: any) => (
        <TruncatedCell>
          {sourceOrder === "order"
            ? record.order_number || "-"
            : generateShodanOrderCode(
                record.delivery_date,
                record.order_number || "-",
              )}
        </TruncatedCell>
      ),
    },
    {
      title: "顧客",
      key: "customer",
      minWidth: 80,
      width: 100,
      render: (_: any, record: any) => (
        <TruncatedCell>
          {record.branch_code && record.branch_code.trim() !== ""
            ? `${record.customer_code} - ${record.branch_code}`
            : record.customer_code || record.customer_id}
        </TruncatedCell>
      ),
    },
    {
      title: "ご担当者名",
      key: "staff",
      minWidth: 100,
      width: 150,
      render: (_: any, record: any) => (
        <TruncatedCell>
          {sourceOrder === "order" ? record.staff_name || "-" : "-"}
        </TruncatedCell>
      ),
    },
    {
      title: "産地",
      key: "origin_name",
      minWidth: 100,
      render: (_: any, record: any) => (
        <TruncatedCell>{getOriginNames(record)}</TruncatedCell>
      ),
    },
    {
      title: "商品",
      key: "product",
      minWidth: 100,
      render: (_: any, record: any) => (
        <TruncatedCell>{getProductNames(record)}</TruncatedCell>
      ),
    },
    {
      title: "出荷日",
      dataIndex: "sales_date",
      key: "sales_date",
      minWidth: 100,
      width: 110,
      render: (_: any, record: any) => (
        <TruncatedCell>
          {sourceOrder === "order"
            ? dayjs(record.sales_date).format("YYYY/MM/DD")
            : dayjs(record.delivery_date).format("YYYY/MM/DD")}
        </TruncatedCell>
      ),
    },
    {
      title: "ステータス",
      key: "status",
      minWidth: 100,
      width: 120,
      render: (_: any, record: any) => (
        <TruncatedCell>
          {sourceOrder === "order" ? (
            <Tag color={getStatusBadgeColor(record.status)}>
              {getStatusInJapanese(record.status)}
            </Tag>
          ) : (
            <Tag color={getStatusBadgeColor(record.status)}>
              {getStatusInJapanese(record.status)}
            </Tag>
          )}
        </TruncatedCell>
      ),
    },
    {
      title: "合計金額",
      dataIndex: "total_amount",
      minWidth: 100,
      width: 130,
      key: "total_amount",
      align: "right" as const,
      className: "price-column",
      render: (_: any, record: any) => {
        const totalAmount =
          sourceOrder === "order"
            ? record.total_amount
            : record.item_list?.reduce(
                (sum: number, item: any) =>
                  sum + (item.order_qty || 0) * (item.proposal_price || 0),
                0,
              );

        return <TruncatedCell>{formatYen(totalAmount)}</TruncatedCell>;
      },
    },
    {
      title: "作成日",
      dataIndex: "created_at",
      key: "created_at",
      minWidth: 100,
      width: 110,
      render: (_: any, record: any) => (
        <TruncatedCell>
          {sourceOrder === "order"
            ? formatApiTimestamp(record.created_at)
            : dayjs(record.item_list[0].update_date).format("YYYY/MM/DD")}
        </TruncatedCell>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 140,
      fixed: screens.lg ? ("right" as const) : undefined,
      render: (_: any, record: any) => {
        const isNWR = sourceOrder === "shodan";
        const isConfirmed =
          record.status === "confirmed" ||
          record.status === "completed" ||
          record.status === "最終確定済み";

        return (
          <Space size="small">
            <Tooltip title="詳細">
              <Button
                type="default"
                shape="circle"
                icon={<EyeOutlined />}
                onClick={() => handleViewOrder(record)}
              />
            </Tooltip>
            <Tooltip title="編集">
              <Button
                type="primary"
                style={{ borderRadius: 50 }}
                icon={<EditOutlined />}
                disabled={isNWR || isConfirmed}
                onClick={() => handleEditOrder(record)}
              />
            </Tooltip>
            <Tooltip title="コピー">
              <Button
                shape="circle"
                icon={<CopyOutlined />}
                onClick={() => handleCopyOrder(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="order-list-content">
      <Card className="list-card" bordered={false}>
        <div className="list-toolbar">
          <Space wrap className="order-list-primary-actions">
            {screens.md && (
              <Button
                size="large"
                icon={<ReloadOutlined />}
                onClick={() =>
                  loadOrders(
                    sourceOrder,
                    currentPage,
                    pageSize,
                    debouncedSearchText,
                    status,
                  )
                }
                loading={loading}
                style={{
                  backgroundColor: "#10b981",
                  borderColor: "#10b981",
                  color: "white",
                  borderRadius: "8px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 20px",
                }}
              >
                更新
              </Button>
            )}

            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={handleNewSingleOrder}
            >
              単品注文
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={handleNewMultiOrder}
            >
              複数商品注文
            </Button>
          </Space>

          <div className="order-list-filters">
            <Select
              placeholder="データソース"
              style={{
                width: 120,
                height: 40,
              }}
              value={sourceOrder}
              onChange={(value) => {
                setOrders([]);
                setSourceOrder(value || "order");
                setCurrentPage(1);
              }}
              options={[
                {
                  label: "ORD-LINE",
                  value: "order",
                },
                {
                  label: "SDO",
                  value: "shodan",
                },
              ]}
            />

            <Select
              allowClear
              placeholder="ステータス"
              style={{
                width: 180,
                height: 40,
              }}
              value={status}
              onChange={(value) => {
                setStatus(value);
                setCurrentPage(1);
              }}
              options={[
                {
                  label: "未確定",
                  value: "pending",
                },
                { label: "最終確認待ち", value: "waiting_final_confirmation" },
                {
                  label: "最終確定済み",
                  value: "confirmed",
                },
              ]}
            />

            <Input
              className="order-list-search"
              placeholder="注文番号や顧客名で検索..."
              prefix={<SearchOutlined />}
              value={searchText}
              style={{
                width: screens.md ? 300 : "100%",
                height: 40,
              }}
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              allowClear
            />
          </div>
        </div>

        {screens.md ? (
          <Table
            key={sourceOrder}
            columns={columns}
            dataSource={orders}
            rowKey={getRowKey}
            loading={loading}
            scroll={{ x: 800 }}
            // tableLayout="fixed"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              size: "middle",
              showTotal: (total, range) =>
                `${total} 件中 ${range[0]}-${range[1]} を表示`,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            onChange={(pagination) => {
              const nextPageSize = pagination.pageSize || 10;
              setCurrentPage(
                nextPageSize !== pageSize ? 1 : pagination.current || 1,
              );
              setPageSize(nextPageSize);
            }}
            className="custom-table"
          />
        ) : (
          <>
            <List
              className="order-mobile-list"
              key={sourceOrder}
              loading={loading}
              itemLayout="vertical"
              dataSource={orders}
              pagination={false}
              renderItem={(order: any) => (
                <List.Item className="order-mobile-list-item">
                  <Card
                    key={getRowKey(order)}
                    className="mobile-order-card"
                    size="small"
                  >
                    <div className="mobile-card-header">
                      <Text strong>
                        {sourceOrder === "order"
                          ? order.order_number || "-"
                          : generateShodanOrderCode(
                              order.delivery_date,
                              order.order_number || "-",
                            )}
                      </Text>
                      <Tag color={getStatusBadgeColor(order.status)}>
                        {getStatusInJapanese(order.status)}
                      </Tag>
                    </div>
                    <div className="mobile-card-body">
                      <p>
                        <UserOutlined />{" "}
                        {order.branch_code && order.branch_code.trim() !== ""
                          ? `${order.customer_code} - ${order.branch_code.trim()}`
                          : order.customer_code || order.customer_id}
                      </p>
                      <p>
                        <UserOutlined /> ご担当者名:{" "}
                        {sourceOrder === "order"
                          ? order.staff_name || "-"
                          : order.staff_name || "-"}
                      </p>
                      <p
                        className="mobile-order-product"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                        }}
                      >
                        <ShoppingOutlined /> {getProductNames(order)}
                      </p>
                      <p>
                        <CalendarOutlined />{" "}
                        {sourceOrder === "order"
                          ? order.sales_date
                            ? dayjs(order.sales_date).format("YYYY/MM/DD")
                            : "-"
                          : order.delivery_date
                            ? dayjs(order.delivery_date).format("YYYY/MM/DD")
                            : "-"}
                      </p>
                      <p>
                        <Text strong style={{ color: "#1890ff" }}>
                          {sourceOrder === "order"
                            ? formatYen(order.total_amount)
                            : formatYen(
                                order.item_list?.reduce(
                                  (acc: any, item: any) =>
                                    acc + item.order_qty * item.proposal_price,
                                  0,
                                ) || 0,
                              )}
                        </Text>
                      </p>
                    </div>
                    <div className="order-mobile-card-actions">
                      <Button
                        type="default"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewOrder(order)}
                      >
                        詳細
                      </Button>
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        disabled={
                          sourceOrder === "shodan" ||
                          order.status === "confirmed" ||
                          order.status === "completed" ||
                          order.status === "最終確定済み"
                        }
                        onClick={() => handleEditOrder(order)}
                      >
                        編集
                      </Button>
                      <Button
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyOrder(order)}
                      >
                        コピー
                      </Button>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
            <MobilePagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              onChange={setCurrentPage}
            />
          </>
        )}
      </Card>
    </div>
  );
};

export default OrderList;
